/**
 * ProductProposalView - renders a product wizard proposal as a structured tree.
 *
 * Displays templates with their attributes, categories hierarchy,
 * and products grouped by template.
 */

import { useState } from 'react'
import { ChevronDown, ChevronRight, Layers, Grid3x3, Package } from 'lucide-react'

interface TemplateProposal {
  name: string
  short_name_format?: string
  long_name_format?: string
  attributes?: AttributeProposal[]
}

interface AttributeProposal {
  code_name: string
  display_name: string
  field_type: string
  attribute_level: string
  visibility?: string
  required_to_publish?: boolean
  configuration_data?: Record<string, unknown>
}

interface CategoryProposal {
  name: string
  slug: string
  children?: CategoryProposal[]
}

interface ProductProposal {
  template_name: string
  category_slug?: string
  attribute_values: Record<string, string>
  variants?: { attribute_values: Record<string, string> }[]
}

interface Proposal {
  templates?: TemplateProposal[]
  categories?: CategoryProposal[]
  products?: ProductProposal[]
}

export function ProductProposalView({ proposal }: { proposal: Record<string, unknown> }) {
  const data = proposal as unknown as Proposal

  return (
    <div className="space-y-6">
      {/* Templates */}
      {data.templates && data.templates.length > 0 && (
        <ProposalSection icon={Layers} title="Templates" count={data.templates.length}>
          {data.templates.map((tmpl, i) => (
            <TemplateCard key={i} template={tmpl} />
          ))}
        </ProposalSection>
      )}

      {/* Categories */}
      {data.categories && data.categories.length > 0 && (
        <ProposalSection icon={Grid3x3} title="Categories" count={countCategories(data.categories)}>
          <CategoryTree categories={data.categories} depth={0} />
        </ProposalSection>
      )}

      {/* Products */}
      {data.products && data.products.length > 0 && (
        <ProposalSection icon={Package} title="Products" count={data.products.length}>
          <div className="space-y-2">
            {data.products.map((prod, i) => (
              <ProductRow key={i} product={prod} />
            ))}
          </div>
        </ProposalSection>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------

function ProposalSection({
  icon: Icon,
  title,
  count,
  children,
}: {
  icon: React.ElementType
  title: string
  count: number
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(true)

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="mb-2 flex w-full cursor-pointer items-center gap-2 text-left"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-[var(--ink-3)]" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-[var(--ink-3)]" />
        )}
        <Icon className="h-3.5 w-3.5 text-[var(--green)]" />
        <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-2)]">
          {title}
        </span>
        <span className="text-[11px] text-[var(--ink-3)]">
          ({count})
        </span>
      </button>
      {open && <div className="ml-5">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Template card
// ---------------------------------------------------------------------------

function TemplateCard({ template }: { template: TemplateProposal }) {
  const [open, setOpen] = useState(true)

  return (
    <div
      className="mb-2 overflow-hidden rounded-[4px] border-[0.5px] border-[var(--rule)]"
    >
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full cursor-pointer items-center gap-2 bg-[var(--bg-subtle)] px-3 py-2 text-left"
      >
        {open ? (
          <ChevronDown className="h-3 w-3 text-[var(--ink-3)]" />
        ) : (
          <ChevronRight className="h-3 w-3 text-[var(--ink-3)]" />
        )}
        <span className="text-[12px] font-medium text-[var(--ink)]">
          {template.name}
        </span>
      </button>
      {open && (
        <div className="px-3 py-2 space-y-2">
          {template.short_name_format && (
            <div className="text-[11px] text-[var(--ink-3)]">
              Format: <code className="font-mono">{template.short_name_format}</code>
            </div>
          )}

          {template.attributes && template.attributes.length > 0 && (
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-[var(--ink-3)]">
                  <th className="text-left font-medium py-1 pr-2">Attribute</th>
                  <th className="text-left font-medium py-1 pr-2">Type</th>
                  <th className="text-left font-medium py-1">Level</th>
                </tr>
              </thead>
              <tbody>
                {template.attributes.map((attr, i) => (
                  <tr key={i} className="text-[var(--ink-2)]">
                    <td className="py-0.5 pr-2">{attr.display_name}</td>
                    <td className="py-0.5 pr-2">
                      <span className="inline-block rounded-[2px] bg-[var(--bg-subtle)] px-1.5 py-0.5 text-[10px] text-[var(--ink-3)]">
                        {attr.field_type}
                      </span>
                    </td>
                    <td className="py-0.5">
                      <span
                        className={`inline-block rounded-[2px] px-1.5 py-0.5 text-[10px] ${
                          attr.attribute_level === 'PRODUCT_VARIANT'
                            ? 'bg-[var(--green-bg-muted)] text-[var(--green)]'
                            : 'bg-[var(--bg-subtle)] text-[var(--ink-3)]'
                        }`}
                      >
                        {attr.attribute_level}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Category tree
// ---------------------------------------------------------------------------

function CategoryTree({ categories, depth }: { categories: CategoryProposal[]; depth: number }) {
  return (
    <div className={depth > 0 ? 'ml-4' : ''}>
      {categories.map((cat, i) => (
        <div key={i}>
          <div className="flex items-center gap-2 py-1 text-[12px] text-[var(--ink-2)]">
            <Grid3x3 className="h-3 w-3 text-[var(--ink-3)]" />
            {cat.name}
            <span className="text-[10px] font-mono text-[var(--ink-3)]">
              {cat.slug}
            </span>
          </div>
          {cat.children && cat.children.length > 0 && (
            <CategoryTree categories={cat.children} depth={depth + 1} />
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Product row
// ---------------------------------------------------------------------------

function ProductRow({ product }: { product: ProductProposal }) {
  const attrs = Object.entries(product.attribute_values)
  const variantCount = product.variants?.length ?? 0

  return (
    <div
      className="rounded-[4px] border-[0.5px] border-[var(--rule)] px-3 py-2"
    >
      <div className="flex items-center justify-between">
        <div className="text-[12px] text-[var(--ink)]">
          {attrs.map(([, v]) => v).join(' - ')}
        </div>
        {variantCount > 0 && (
          <span className="text-[10px] text-[var(--ink-3)]">
            {variantCount} variant{variantCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>
      <div className="mt-0.5 text-[10px] text-[var(--ink-3)]">
        {product.template_name}
        {product.category_slug ? ` / ${product.category_slug}` : ''}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function countCategories(cats: CategoryProposal[]): number {
  return cats.reduce(
    (sum, cat) => sum + 1 + (cat.children ? countCategories(cat.children) : 0),
    0,
  )
}
